import { inject, Injectable } from '@angular/core';
import { OpenIdConnectApiService } from '@core/sso/services/open-id-connect/open-id-connect-api.service';
import { AuthorizationCodeRequest } from '@core/sso/models/open-id-connect/authorization-code-request.interface';
import { AuthorizationRequest } from '@core/sso/models/open-id-connect/authorization-request.interface';
import { SubmitFormService } from '@core/sso/services/submit-form.service';
import { RedirectParamKey } from '@core/sso/models/open-id-connect/redirect-param-key.enum';
import { ResponseMode } from '@core/sso/models/open-id-connect/response-mode.enum';
import { AuthorizationErrorCode } from '@core/sso/models/open-id-connect/authorization-error-code.enum';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { RedirectOptions } from '@core/sso/models/open-id-connect/redirect-options.interface';
import { asNonUndefined, WINDOW } from '@kaseya-utils';
import { Router } from '@angular/router';
import { TenantsFacade } from '@core/sso/facades/tenants.facade';
import { ToastService } from '@core/toast/toast.service';
import { skipWhileUndefined } from '@shared/rxjs-operators/skip-while-undefined';

@Injectable({ providedIn: 'root' })
export class OpenIdConnectService {
  private readonly openIdConnectApiService = inject(OpenIdConnectApiService);
  private readonly submitFormService = inject(SubmitFormService);
  private readonly router = inject(Router);
  private readonly tenantsFacade = inject(TenantsFacade);
  private readonly toastService = inject(ToastService);
  private readonly window = inject(WINDOW);

  initAuthorizationCodeFlow(authorizationRequest: AuthorizationRequest): void {
    if (authorizationRequest.login_hint) {
      this.initWithLoginHint(authorizationRequest);
      return;
    }

    if (authorizationRequest.tenant_selection === 'skip') {
      this.authorize(authorizationRequest, null);
      return;
    }

    this.tenantsFacade.loadTenants(authorizationRequest.client_id);

    this.tenantsFacade.tenants$.pipe(skipWhileUndefined()).subscribe(tenants => {
      if (!tenants.length) {
        this.authorize(authorizationRequest, null);
        return;
      }

      if (tenants.length > 1) {
        this.initWithSelectedTenant(authorizationRequest);
        return;
      }

      this.authorize(authorizationRequest, tenants[0].productTenantMappingId);
    });
  }

  private initWithSelectedTenant(authorizationRequest: AuthorizationRequest): void {
    this.router.navigate(['connect', 'tenants']);
    this.tenantsFacade.selectedTenant$.pipe(skipWhileUndefined()).subscribe(({ productTenantMappingId }) => {
      this.authorize(authorizationRequest, productTenantMappingId);
    });
  }

  private initWithLoginHint(authorizationRequest: AuthorizationRequest): void {
    this.openIdConnectApiService
      .getTenantByLoginHint(authorizationRequest.client_id, asNonUndefined(authorizationRequest.login_hint))
      .subscribe({
        next: ({ productTenantMappingId }) => {
          this.authorize(authorizationRequest, productTenantMappingId);
        },
        error: (httpErrorResponse: HttpErrorResponse) => {
          if (httpErrorResponse.status === HttpStatusCode.NotFound) {
            this.toastService.showErrorToast('tenants.errors.not-found');
          }
          if (httpErrorResponse.status === HttpStatusCode.BadRequest && httpErrorResponse.error === 'InvalidLoginHint') {
            this.toastService.showErrorToast('tenants.errors.invalid-login-hint');
          }
          if (httpErrorResponse.status === HttpStatusCode.Forbidden) {
            this.redirectToProductAccessDeniedPage();
          }
        },
      });
  }

  private authorize(authorizationRequest: AuthorizationRequest, productTenantMappingId: string | null): void {
    const authorizationCodeRequest: AuthorizationCodeRequest = {
      clientId: authorizationRequest.client_id,
      scope: authorizationRequest.scope,
      responseType: authorizationRequest.response_type,
      redirectUri: authorizationRequest.redirect_uri,
      responseMode: authorizationRequest.response_mode ?? null,
      state: authorizationRequest.state ?? null,
      nonce: authorizationRequest.nonce ?? null,
      productTenantMappingId,
    };

    this.openIdConnectApiService.getAuthorizationCode(authorizationCodeRequest).subscribe({
      next: ({ code, state }) => {
        this.redirect(
          {
            redirectUri: authorizationCodeRequest.redirectUri,
            code,
            state,
            error: null,
          },
          authorizationCodeRequest.responseMode
        );
      },
      error: (errorResponse: HttpErrorResponse) => {
        if (errorResponse.status === HttpStatusCode.Forbidden) {
          this.redirectToProductAccessDeniedPage();
          return;
        }

        const error = this.mapErrorResponseToAuthorizationErrorCode(errorResponse);

        if (error === AuthorizationErrorCode.InvalidOpenIdConnectClient || error === AuthorizationErrorCode.RedirectUriDoesNotMatch) {
          return this.toastService.showErrorToast('login.messages.improper-configuration');
        }

        this.redirect(
          {
            error,
            redirectUri: authorizationCodeRequest.redirectUri,
            code: null,
            state: authorizationCodeRequest.state,
          },
          authorizationCodeRequest.responseMode
        );
      },
    });
  }

  private redirect(redirectOptions: RedirectOptions, responseMode: string | null): void {
    if (!responseMode || responseMode === ResponseMode.Query) {
      this.redirectByLocationReplace(redirectOptions);
      return;
    }
    if (responseMode === ResponseMode.FormPost) {
      this.redirectByForm(redirectOptions);
    }
  }

  private redirectByLocationReplace(redirectOptions: RedirectOptions): void {
    const url = new URL(redirectOptions.redirectUri);
    if (redirectOptions.code) {
      url.searchParams.append(RedirectParamKey.Code, redirectOptions.code);
    }
    if (redirectOptions.state) {
      url.searchParams.append(RedirectParamKey.State, redirectOptions.state);
    }
    if (redirectOptions.error) {
      url.searchParams.append(RedirectParamKey.Error, redirectOptions.error);
    }
    this.window.location.replace(url.href);
  }

  private redirectByForm(redirectOptions: RedirectOptions): void {
    this.submitFormService.submitForm({
      method: 'post',
      target: '_self',
      action: redirectOptions.redirectUri,
      inputs: [
        {
          type: 'hidden',
          name: RedirectParamKey.Code,
          value: redirectOptions.code,
        },
        {
          type: 'hidden',
          name: RedirectParamKey.Error,
          value: redirectOptions.error,
        },
        {
          type: 'hidden',
          name: RedirectParamKey.State,
          value: redirectOptions.state,
        },
      ],
    });
  }

  private mapErrorResponseToAuthorizationErrorCode(errorResponse: HttpErrorResponse): AuthorizationErrorCode {
    if (errorResponse.status === HttpStatusCode.Unauthorized) {
      return AuthorizationErrorCode.UnauthorizedClient;
    }

    if (errorResponse.status === HttpStatusCode.Forbidden) {
      return AuthorizationErrorCode.AccessDenied;
    }

    if (errorResponse.status === HttpStatusCode.BadRequest && errorResponse.error === 'UnsupportedResponseType') {
      return AuthorizationErrorCode.UnsupportedResponseType;
    }

    if (errorResponse.status === HttpStatusCode.BadRequest && errorResponse.error === 'OpenIdScopeNotPresent') {
      return AuthorizationErrorCode.InvalidScope;
    }

    if (errorResponse.status === HttpStatusCode.BadRequest && errorResponse.error === 'UnsupportedScope') {
      return AuthorizationErrorCode.InvalidScope;
    }

    if (errorResponse.status === HttpStatusCode.BadRequest && errorResponse.error === 'UnsupportedResponseMode') {
      return AuthorizationErrorCode.UnsupportedResponseMode;
    }

    if (errorResponse.status === HttpStatusCode.BadRequest && errorResponse.error === 'InvalidOpenIdConnectClient') {
      return AuthorizationErrorCode.InvalidOpenIdConnectClient;
    }

    if (errorResponse.status === HttpStatusCode.BadRequest && errorResponse.error === 'RedirectUriDoesNotMatch') {
      return AuthorizationErrorCode.RedirectUriDoesNotMatch;
    }

    if (errorResponse.status === HttpStatusCode.BadRequest) {
      return AuthorizationErrorCode.InvalidRequest;
    }

    if (errorResponse.status === HttpStatusCode.ServiceUnavailable) {
      return AuthorizationErrorCode.TemporarilyUnavailable;
    }

    if (errorResponse.status === HttpStatusCode.InternalServerError) {
      return AuthorizationErrorCode.ServerError;
    }

    return AuthorizationErrorCode.InvalidRequest;
  }

  private redirectToProductAccessDeniedPage(): void {
    this.router.navigate(['product-access-denied']);
  }
}
